<template>
  <div class="form-group">
    <label class="label-option">
      {{ headingLabel }}
      <span
        class="title-table-presentation-tooltip result-layout-tooltip tooltip-on-hover"
        v-if="isNotTextAndBulletAndLeaderboardSlide"
      >
        <span
          v-tooltip="slideTitleTooltip"
          class="tooltip-btn"
        />
        <i class="fas fa-question-circle" />
      </span>

      <TextAlignEdit />
      <HintButton v-if="isHintButtonVisible" />
    </label>
    <div class="mb-2 input-group input-option relative">
      <InputText
        :val="slideActive.title"
        @interface="updateSlide"
        :place-holder="headingPlaceholder"
        spellcheck="false"
        :id-name="'title-slide-edit'"
        :limit="300"
        type="input-title"
        :inputId="`slide-title-${slideActive.id}`"
        :key="slideActive.randomIndexId || 'title_slide' + keyUndoRedo"
      />
      <QuestionImageButton v-if="showImageInputField" />
    </div>
    <QuestionRecommendations v-if="canUseQuestionSuggestions" />
    <!-- Question description -->
    <QuestionDescription
      v-show="titleDescVisible"
      v-if="reloadTitleDesc"
      @updateSlide="updateSlide"
      :reloadTitleDesc="reloadTitleDesc"
      :keyUndoRedo="keyUndoRedo"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import Socket from '@/socket'
import utils from '@/util'
import SlideApi from '@/api/slide'
import eventCategories from '@/constant/eventCategories'

// components
import InputText from '@/components/presentations/InputText'
import TextAlignEdit from '@/components/presentations/TextAlignEdit.vue'
import QuestionImageButton from '@/components-v2/presentation-editor/side-right/QuestionImageButton.vue'
import QuestionDescription from '@/components-v2/presentation-editor/side-right/QuestionDescription.vue'
import HintButton from '@/components-v2/presentation-editor/side-right/HintButton.vue'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import UpdateTitleSlideMixin from '@/mixins/undo-redo/UpdateTitleSlideMixin'
import EventBus from '@/eventBus'
import GoogleSlideService from '@/services/google-slide'
import QuestionRecommendations from '@/components-v2/presentation-editor/side-right/QuestionRecommendations.vue'

export default {
  name: 'SlideTitleSettings',
  components: {
    QuestionRecommendations,
    InputText,
    TextAlignEdit,
    QuestionImageButton,
    QuestionDescription,
    HintButton,
  },
  mixins: [UndoRedoMixin, UpdateTitleSlideMixin],
  props: {
    keyUndoRedo: {
      type: [String, Number],
      default: null,
    },
    showImageInputField: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      reloadTitleDesc: true,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapGetters('featureToggles', [
      'canUseQuestionSuggestions',
    ]),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      integrationPlatform: (state) => state.integrationPlatform,
    }),
    headingLabel() {
      const { type } = this.slideActive
      if (type === 'bulletPoints' || type === 'textSlide' || type == 'question') {
        return this.$i18nPresenter.t('Heading')
      }
      if (type === 'leaderboard') {
        return this.$i18nPresenter.t('Leaderboard title')
      }
      return this.$i18nPresenter.t('Your question')
    },

    isNotTextAndBulletAndLeaderboardSlide() {
      const slideTypes = ['textSlide', 'bulletPoints', 'leaderboard']
      return !slideTypes.includes(this.slideActive.type)
    },

    slideTitleTooltip() {
      if (this.slideActive.type === 'pickAnswer') {
        return this.$i18nPresenter.t('Enter the question you want to ask your players')
      }
      return this.$i18nPresenter.t('Enter the question you want to ask your audience')
    },

    headingPlaceholder() {
      const { type } = this.slideActive
      if (type === 'multipleChoice' || type === 'imageChoice') {
        return this.$i18nPresenter.t('Your poll question…')
      }
      if (type === 'bulletPoints' || type === 'textSlide') {
        return this.$i18nPresenter.t('Slide heading…')
      }
      if (type === 'openEnded') {
        return this.$i18nPresenter.t('Your open ended question…')
      }
      if (type === 'leaderboard') {
        return this.$i18nPresenter.t('Leaderboard')
      }
      if (type === 'question') {
        return this.$i18nPresenter.t('Heading for your Q&A session…')
      }
      if (type === 'scale') {
        return this.$i18nPresenter.t('Your scale question…')
      }
      if (type === 'ideas') {
        return this.$i18nPresenter.t('Write your idea…')
      }
      return this.$i18nPresenter.t('Your question…')
    },

    titleDescVisible() {
      const slideTypes = ['scale', 'multipleChoice', 'openEnded', 'wordCloud', 'imageChoice',
        'question', 'pickAnswer', 'ideas']
      return slideTypes.includes(this.slideActive.type)
    },

    isHintButtonVisible() {
      return (this.slideActive.type === 'pickAnswer' && (!this.slideActive.slideType || this.slideActive.slideType === 'imageChoice')) &&
      !this.presentation.audiencePacing
    },
  },
  mounted() {
    this.registerHistory()
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'updateSlideActiveAction']),
    updateSlide(data, isBackup = true) {
      if (isBackup) {
        this.backupUpdateSlide(data)
      }
      const payload = {}
      if (data.value) {
        payload.title = data.value
        payload.titleHTML = data.value
      } else if (data.value === '') {
        payload.title = ' '
        payload.titleHTML = ' '
      }

      if (data.titleDesc && data.titleDesc.replace(/\s/g, '') != '') {
        payload.titleDesc = data.titleDesc
      } else if (data.titleDesc === '') {
        payload.titleDesc = null
      }
      this.setSyncLoadingAction(1)

      payload.presentationId = this.presentation.id
      payload.accessCode = this.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      payload.presenting = this.isPresenting || this.isBackstage

      this.updateSlideActiveAction({
        ...payload,
      })
      this._updateSlide(payload)

      const slideType = this.slideActive.type === 'pickAnswer' ? this.slideActive.slideType : this.slideActive.type
      if (payload.title) {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Edit slide title',
          'Right column',
          `Slide type: ${slideType}`,
          {
            slideId: this.slideActive.id,
          },
        )
      }

      if (payload.titleDesc || payload.titleDesc === null) {
        utils.mixpanelTracking(
          'Slide',
          'Edit text slide',
          'Done editing question description',
        )
      }
    },

    async _updateSlide(payload) {
      try {
        await SlideApi.updateSlideById(this.slideActive.id, payload)
        this.setSyncLoadingAction(2)
        console.log('enqueueUpdateTitle', payload.title)

        if (this.integrationPlatform === 'GoogleSlide' && (payload.title || payload.title === ' ')) {
          GoogleSlideService.enqueueUpdateTitle(payload.title)
        }
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },

    callbackAfterRestore(data) {
      if (data.value !== undefined) {
        EventBus.$emit(`update-right-column-for-slide-title-${this.slideActive.id}`, data.value)
      }
      if (data.idName === 'title-desc-edit') {
        this.reloadTitleDesc = false
        setTimeout(() => {
          this.reloadTitleDesc = true
        }, 100)
      }
    },
  },
}
</script>
